(() => {
  'use strict';
  const core = window.DOISearchCore;
  const norm = value => core ? core.norm(value) : String(value || '').toLowerCase().trim();
  const path = value => {
    try {
      const url = new URL(value, document.baseURI);
      return url.origin === location.origin && /^\/(pages|products|blogs|collections)\//.test(url.pathname) ? (window.DOIHistoryURL?.pathname(url.pathname) || url.pathname).replace(/\/$/, '') : '';
    } catch { return ''; }
  };
  function picture(value) {
    if (!value) return '';
    try {
      const url = new URL(value, document.baseURI);
      if (url.protocol !== 'https:') return '';
      if (url.hostname === 'cdn.shopify.com' || url.origin === location.origin) url.searchParams.set('width', '800');
      return url.href;
    } catch { return ''; }
  }
  function record(raw) {
    const key = path(raw.url || raw.key);
    if (!key || typeof raw.title !== 'string' || !raw.title.trim()) return null;
    return {key, title: raw.title, summary: raw.summary || '', image: picture(raw.image), category: raw.category || '',
      regions: Array.isArray(raw.regions) ? raw.regions : [], text: raw.text || raw.title, date: raw.date || '',
      featured: raw.featured === true, source: raw.source || raw.kind || '', profile: raw.profile || null,
      keywords: Array.isArray(raw.keywords) ? raw.keywords : [], video: window.DOIAtlasVideo?.normalize(raw.video) || null};
  }
  const kindConfigs = new WeakMap();
  function resourceKind(item, root) {
    let config = kindConfigs.get(root);
    if (!config) {
      config = {labels: {}, resources: new Map()};
      try {
        const data = JSON.parse(root.querySelector('[data-ac-resource-kinds]')?.textContent || '{}');
        config.labels = typeof data.labels === 'string' ? JSON.parse(data.labels) : data.labels || {};
        for (const entry of data.resources || []) {
          const key = path(entry.path);
          if (key && typeof entry.kind === 'string' && entry.kind.trim()) config.resources.set(key, entry.kind);
        }
      } catch {}
      kindConfigs.set(root, config);
    }
    const key = path(item.key || item.url), custom = config.resources.get(key);
    if (custom) return custom;
    const tool = {'/pages/coachella-valley-interactive-map': 'map', '/pages/coachella-valley-trip-planner': 'trip',
      '/pages/coachella-valley-community-explorer': 'communities', '/pages/coachella-valley-restaurant-guide': 'dining'}[key];
    const source = item.source || item.searchRecord?.kind || (key.startsWith('/blogs/') ? 'article' : key.startsWith('/products/') ? 'product' : key.startsWith('/collections/') ? 'collection' : 'page');
    const type = tool || (source === 'article' ? 'story' : source === 'product' && ['nature', 'people', 'community'].includes(item.category) ? item.category : source);
    return config.labels[type] || config.labels.resource || 'Resource';
  }
  function prepared(item) {
    if (!core) return null;
    if (item.searchRecord) return item.searchRecord;
    const kind = item.source || (item.key.startsWith('/blogs/') ? 'article' : item.key.startsWith('/products/') ? 'product' : item.key.startsWith('/collections/') ? 'collection' : 'page');
    item.searchRecord = core.prepare({url: item.key, title: item.title, summary: item.summary, image: item.image,
      kind, profile: item.profile, tags: item.text, search_text: item.text, search_aliases: item.keywords});
    return item.searchRecord;
  }
  function matches(item, info) {
    const row = prepared(item);
    return row ? core.match(row, info) : norm(item.title + ' ' + item.text).includes(norm(info.original));
  }
  function rank(items, info) {
    const scores = new Map(items.map(item => [item.key, core && prepared(item) ? core.score(prepared(item), info) : Number(norm(item.title) === norm(info.original)) * 1500]));
    return items.sort((a, b) => scores.get(b.key) - scores.get(a.key) || a.title.localeCompare(b.title));
  }
  function fromSearch(row, existing) {
    let category = 'lifestyle';
    if (row.url.startsWith('/blogs/history/')) category = 'history';
    else if (row.url.startsWith('/blogs/nature/')) category = 'nature';
    else if (row.url.startsWith('/blogs/vacation-rentals/')) category = 'vacation-rentals';
    else if (row.kind === 'product') category = ['bird_guides', 'plant_guides', 'wildlife_guides', 'mountain_guides'].includes(row.labelKey) ? 'nature' : row.labelKey === 'people' ? 'people' : row.profile?.content_type === 'community' || row.type === 'plans' || row.primary ? 'community' : 'place';
    else if (row.market || row.profile?.content_type === 'community') category = 'community';
    const r = record({url: row.url, title: row.title, summary: row.summary, image: row.image, category,
      regions: [...new Set([...(row.cities || []), ...(row.coverage || [])])].map(city => norm(city).replace(/ /g, '-')),
      source: row.kind, profile: row.profile, text: [row.title, row.summary, row.tags].filter(Boolean).join(' ')});
    if (!r) return null;
    if (existing) Object.assign(r, existing);
    r.searchRecord = row;
    r.target = row.targetUrl || row.url;
    return r;
  }
  class Catalog {
    constructor({root, onRecords, onState}) {
      this.root = root; this.onRecords = onRecords; this.onState = onState;
      this.complete = false; this.error = false; this.loading = false;
      this.cacheKey = 'doi-atlas-catalog-v2:' + root.dataset.catalogKey;
      this.hasCache = false;
      const early = window.DOIAtlasCatalogStartup;
      const startup = early?.key === this.cacheKey && early.url === root.dataset.catalogSeedUrl ? early : null;
      this.seedRequest = startup?.seed || null;
      if (startup) delete window.DOIAtlasCatalogStartup;
      try {
        const saved = startup ? startup.cached : JSON.parse(sessionStorage.getItem(this.cacheKey) || 'null');
        if (saved?.version === 2 && Date.now() - saved.time < 300000 && Array.isArray(saved.records) && saved.records.length > 0 && saved.records.length <= 10000) {
          const rows = saved.records.map(record).filter(Boolean);
          if (rows.length === saved.records.length) {
            this.complete = true; this.hasCache = true; this.root.dataset.catalogSource = 'cache'; this.onRecords(rows, true);
          }
        }
      } catch {}
    }
    notify() {
      this.root.dataset.directoryState = this.complete ? 'ready' : this.error ? 'error' : 'loading';
      this.onState();
    }
    async seed() {
      const pending = this.seedRequest;
      this.seedRequest = null;
      let data = pending ? await pending : null;
      if (!data) {
        const response = await fetch(this.root.dataset.catalogSeedUrl, {credentials: 'omit', signal: AbortSignal.timeout(5000)});
        if (!response.ok) return;
        data = await response.json();
      }
      if (this.complete || data.version !== 2 || !Array.isArray(data.records) || !data.records.length || data.records.length > 10000) return;
      const rows = data.records.map(record).filter(Boolean);
      if (rows.length !== data.records.length) return;
      this.complete = true; this.hasCache = true; this.error = false; this.root.dataset.catalogSource = 'seed';
      this.onRecords(rows, true); this.notify();
    }
    async page(number, parameter = 'page') {
      const url = new URL(this.root.dataset.directoryUrl, document.baseURI);
      url.search = ''; url.hash = '';
      url.searchParams.set('view', 'doi-atlas-search-data');
      url.searchParams.set(parameter, String(number));
      const preview = new URL(document.baseURI).searchParams.get('preview_theme_id') || document.body.dataset.doiPreviewTheme;
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, {credentials: 'same-origin', signal: AbortSignal.timeout(15000)});
      if (!response.ok) throw Error('Atlas catalog unavailable');
      const data = await response.json();
      if (data.version !== 2 || data.page !== number || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 100 || !/^page(?:_[a-zA-Z0-9]+)?$/.test(data.param) || !Array.isArray(data.records)) throw Error('Invalid Atlas catalog');
      return data;
    }
    async start() {
      if (this.loading) return;
      this.loading = true; this.error = false; this.notify();
      if (!this.hasCache && this.root.dataset.catalogSeedUrl) this.seed().catch(() => {});
      const fresh = new Map();
      try {
        const first = await this.page(1);
        let loaded = 0;
        const accept = data => {
          if (data.pages !== first.pages || data.param !== first.param || data.total !== first.total) throw Error('Atlas catalog changed during loading');
          const rows = data.records.map(record).filter(Boolean);
          rows.forEach(row => fresh.set(row.key, row));
          this.root.dataset.directoryLoadedPages = String(++loaded);
          if (!this.hasCache) this.onRecords(rows.map(row => ({...row})), false);
        };
        accept(first);
        let next = 2;
        const worker = async () => { while (next <= first.pages) accept(await this.page(next++, first.param)); };
        const workers = await Promise.allSettled(Array.from({length: Math.min(3, first.pages - 1)}, worker));
        if (workers.some(result => result.status === 'rejected')) throw Error('Atlas catalog incomplete');
        this.complete = true; this.root.dataset.catalogSource = 'fresh'; this.onRecords([...fresh.values()].map(row => ({...row})), true);
        try {
          const payload = JSON.stringify({version: 2, time: Date.now(), records: [...fresh.values()]});
          if (payload.length < 2500000) sessionStorage.setItem(this.cacheKey, payload);
        } catch {}
      } catch { this.error = !this.complete; }
      finally { this.loading = false; this.notify(); }
    }
  }
  class Search {
    constructor({root, getRecords, onCommit, onUpdate, closeMobile, transformRecord = row => row}) {
      this.root = root; this.input = root.querySelector('[data-ac-search]'); this.form = root.querySelector('[data-ac-search-form]');
      this.popup = root.querySelector('[data-ac-suggestions]'); this.list = root.querySelector('[data-ac-suggestion-list]');
      this.note = root.querySelector('[data-ac-suggestion-status]'); this.copy = JSON.parse(root.querySelector('[data-ac-copy]').textContent);
      this.getRecords = getRecords; this.transformRecord = transformRecord; this.onCommit = onCommit; this.onUpdate = onUpdate; this.closeMobile = closeMobile;
      this.query = this.input.value.trim(); this.results = new Map(); this.draftResults = new Map();
      this.busy = false; this.error = false; this.limited = false; this.active = -1; this.options = [];
      this.allowed = false; this.generation = 0;
      this.form.addEventListener('submit', event => { event.preventDefault(); this.commit(); });
      this.input.addEventListener('input', event => { if (!event.isComposing) this.input.value.trim() ? this.suggest() : this.commit(''); });
      this.input.addEventListener('compositionend', () => this.suggest());
      this.input.addEventListener('focus', () => this.suggest());
      this.input.addEventListener('keydown', event => this.keydown(event));
      this.form.addEventListener('focusout', () => queueMicrotask(() => { if (!this.form.contains(document.activeElement)) this.close(); }));
      document.addEventListener('pointerdown', event => { if (!this.form.contains(event.target)) this.close(); });
    }
    info(query = this.query) { return core ? core.resolve(query) : {original: query}; }
    collect(query, extra = this.results, titlesOnly = false) {
      const items = new Map(this.getRecords());
      for (const [key, row] of extra) items.set(key, this.getRecords().has(key) ? {...this.getRecords().get(key), searchRecord: row.searchRecord, target: row.target} : row);
      const info = this.info(query);
      const rows = new Map();
      for(const source of items.values()){
        if(source.key==='/pages/coachella-valley-atlas'||!matches(source,info))continue;
        if(titlesOnly){
          const search=prepared(source);
          if(!(search?info.terms.every(term=>search.actualTitleTokens.some(word=>word===term||term.length>=3&&word.startsWith(term))):norm(source.title).includes(norm(query))))continue;
        }
        const row=this.transformRecord(source);
        if(row&&!rows.has(row.key))rows.set(row.key,row);
      }
      return rank([...rows.values()],info);
    }
    task(query) {
      const controller = new AbortController();
      const task = {query: norm(query), controller};
      task.promise = window.DOISearch?.quick ? window.DOISearch.quick(query, {signal: controller.signal}) : Promise.reject(Error('Search unavailable'));
      return task;
    }
    updateRows(rows, target) {
      for (const row of rows) {
        const r = fromSearch(row, this.getRecords().get(path(row.url)));
        if (r) target.set(r.key, r);
      }
    }
    suggest() {
      clearTimeout(this.timer);
      if (this.draftTask && this.draftTask !== this.resultTask) this.draftTask.controller.abort();
      this.draftTask = null; this.draftResults = new Map(); this.draftBusy = false; this.active = -1;
      this.allowed = norm(this.input.value).length >= 3;
      this.refresh();
      if (!this.allowed || this.input.isComposing) return;
      const query = this.input.value.trim();
      this.timer = setTimeout(() => {
        const task = this.task(query); this.draftTask = task; this.draftBusy = true; this.refresh();
        task.promise.then(data => {
          if (this.draftTask !== task || norm(this.input.value) !== task.query) return;
          this.updateRows(data.items, this.draftResults);
        }).catch(() => {}).finally(() => {
          if (this.draftTask === task) { this.draftBusy = false; this.refresh(); }
        });
      }, 150);
    }
    refresh() {
      if (!this.allowed || document.activeElement !== this.input || norm(this.input.value).length < 3) { this.close(); return; }
      const query = this.input.value.trim();
      const previous = this.options[this.active]?.query;
      this.options = this.collect(query, this.draftResults, true).slice(0, 6).map(row => ({query: row.title, row}));
      if (!this.options.some(option => norm(option.query) === norm(query))) this.options.push({query});
      this.list.replaceChildren();
      for (const [index, option] of this.options.entries()) {
        const node = document.createElement('div'); node.className = 'ac-suggestion'; node.id = 'ac-suggestion-' + index;
        node.setAttribute('role', 'option'); node.setAttribute('aria-selected', 'false');
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg'); icon.setAttribute('viewBox', '0 0 24 24'); icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = '<circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/>';
        const text = document.createElement('span'); text.className = 'ac-suggestion-copy';
        const title = document.createElement('span'); title.className = 'ac-suggestion-title';
        title.textContent = option.row ? option.query : this.copy.search_for.replace('__QUERY__', query);
        text.append(title);
        node.append(icon, text);
        node.addEventListener('mousedown', event => event.preventDefault());
        node.addEventListener('click', () => this.commit(option.query));
        this.list.append(node);
      }
      this.popup.hidden = false; this.input.setAttribute('aria-expanded', 'true');
      this.note.textContent = this.draftBusy ? this.copy.searching : '';
      this.note.hidden = !this.draftBusy;
      this.active = previous ? this.options.findIndex(option => option.query === previous) : -1;
      this.select(this.active);
    }
    select(index) {
      this.active = index;
      [...this.list.children].forEach((node, i) => node.setAttribute('aria-selected', String(i === index)));
      if (index < 0) this.input.removeAttribute('aria-activedescendant');
      else {
        const node = this.list.children[index]; this.input.setAttribute('aria-activedescendant', node.id);
        node.scrollIntoView({block: 'nearest'});
      }
    }
    keydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape' && !this.popup.hidden) { event.preventDefault(); event.stopPropagation(); this.close(); return; }
      if (event.key === 'Tab') { this.close(); return; }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (this.popup.hidden) { this.allowed = true; this.refresh(); }
        if (!this.options.length || this.popup.hidden) return;
        event.preventDefault();
        this.select(event.key === 'ArrowDown' ? (this.active + 1) % this.options.length : (this.active <= 0 ? this.options.length - 1 : this.active - 1));
      }
    }
    close() { this.allowed = false; this.popup.hidden = true; this.input.setAttribute('aria-expanded', 'false'); this.input.removeAttribute('aria-activedescendant'); this.active = -1; }
    async commit(value) {
      const query = (value ?? this.options[this.active]?.query ?? this.input.value).trim().slice(0, 160);
      this.input.value = query; this.query = query; clearTimeout(this.timer); this.close();
      const generation = ++this.generation;
      if (this.resultTask && this.resultTask !== this.draftTask) this.resultTask.controller.abort();
      this.results = new Map(); this.busy = !!query; this.error = false; this.limited = false;
      this.onCommit(query); this.onUpdate();
      if (!query) { this.resultTask?.controller.abort(); this.resultTask = null; return; }
      const task = this.draftTask?.query === norm(query) && !this.draftTask.controller.signal.aborted ? this.draftTask : this.task(query);
      this.resultTask = task; this.closeMobile();
      try {
        const data = await task.promise;
        if (generation !== this.generation) return;
        this.updateRows(data.items, this.results); this.limited = data.pages > 10; this.onUpdate();
        let next = 2;
        const worker = async () => {
          while (next <= Math.min(data.pages, 10) && generation === this.generation) {
            const result = await window.DOISearch.quick(query, {pageNumber: next++, signal: task.controller.signal});
            if (generation !== this.generation) return;
            this.updateRows(result.items, this.results); this.onUpdate();
          }
        };
        const jobs = await Promise.allSettled(Array.from({length: Math.min(3, Math.max(0, Math.min(data.pages, 10) - 1))}, worker));
        if (jobs.some(job => job.status === 'rejected')) this.error = true;
      } catch (error) { if (generation === this.generation && !task.controller.signal.aborted) this.error = true; }
      finally { if (generation === this.generation) { this.busy = false; this.onUpdate(); } }
    }
  }
  window.DOIAtlasSearch = {Catalog, Search, record, prepared, matches, rank, resourceKind};
})();

